/**
 * Terminbuch der Kasse (Modul „termine“): Zeitrechnung in Wiener Ortszeit
 * (Regel 6) und Lage der Termine im Tagesraster.
 */

const FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Vienna',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function teile(d: Date): { tag: string; stunde: number; minute: number; utcAlsWien: number } {
  const p = FORMAT.formatToParts(d);
  const w = (t: string): number => Number(p.find((x) => x.type === t)?.value ?? '0');
  const jahr = w('year');
  const monat = w('month');
  const tagZahl = w('day');
  const stunde = w('hour');
  const minute = w('minute');
  return {
    tag: `${String(jahr)}-${String(monat).padStart(2, '0')}-${String(tagZahl).padStart(2, '0')}`,
    stunde,
    minute,
    utcAlsWien: Date.UTC(jahr, monat - 1, tagZahl, stunde, minute),
  };
}

/** Wiener Tag (JJJJ-MM-TT) eines Zeitpunkts. */
export function wienerTag(d: Date): string {
  return teile(d).tag;
}

/** Zeitpunkt aus Wiener Tag und Uhrzeit (HH:MM), auch an Umstellungstagen richtig. */
export function wienerZeitpunkt(tag: string, uhrzeit: string): Date {
  const [j, m, t] = tag.split('-').map(Number);
  const [h, mi] = uhrzeit.split(':').map(Number);
  const ziel = Date.UTC(j ?? 0, (m ?? 1) - 1, t ?? 1, h ?? 0, mi ?? 0);
  let utc = ziel;
  for (let i = 0; i < 3; i++) {
    const versatz = teile(new Date(utc)).utcAlsWien - utc;
    utc = ziel - versatz;
  }
  return new Date(utc);
}

/** Minuten seit 0:00 Wiener Zeit. */
export function wienerMinuten(d: Date): number {
  const t = teile(d);
  return t.stunde * 60 + t.minute;
}

export function uhrzeitText(minuten: number): string {
  return `${String(Math.floor(minuten / 60)).padStart(2, '0')}:${String(minuten % 60).padStart(2, '0')}`;
}

/** Tag ± n Tage (Kalendertage, unabhängig von der Uhrzeit). */
export function tagVerschieben(tag: string, n: number): string {
  const [j, m, t] = tag.split('-').map(Number);
  const d = new Date(Date.UTC(j ?? 0, (m ?? 1) - 1, (t ?? 1) + n));
  return d.toISOString().slice(0, 10);
}

export function tagText(tag: string): string {
  const [j, m, t] = tag.split('-').map(Number);
  return new Date(Date.UTC(j ?? 0, (m ?? 1) - 1, t ?? 1, 12)).toLocaleDateString('de-AT', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** Lage im Tagesraster: Oberkante und Höhe in Rasterzeilen (Viertelstunden ab `startStunde`). */
export function lageImRaster(
  beginn: Date,
  dauerMin: number,
  startStunde: number,
  endStunde: number,
): { zeile: number; zeilen: number } | null {
  const von = wienerMinuten(beginn) - startStunde * 60;
  const bis = von + dauerMin;
  const max = (endStunde - startStunde) * 60;
  if (bis <= 0 || von >= max) {
    return null;
  }
  const a = Math.max(0, von);
  const e = Math.min(max, bis);
  return { zeile: Math.floor(a / 15), zeilen: Math.max(1, Math.ceil((e - a) / 15)) };
}
